import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { generateArticleSchema } from '@/components/feature/SchemaOrg';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = getArticleById(id || '');

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-2xl bg-background-100">
              <i className="ri-error-warning-line text-4xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground-950 mb-3">文章未找到</h1>
            <p className="text-foreground-600 mb-6">您要查看的文章可能已被移除或链接失效。</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = getRelatedArticles(article);
  const articleSchema = generateArticleSchema(article);

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://985huidian.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
      />
      <SchemaOrg type="Organization" />
      <SchemaOrg type="WebSite" />
      <SchemaOrg type="Article" data={articleSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: article.categoryName, href: `/category/${article.category}` },
            { label: article.title },
          ]}
        />

        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-10">
            {/* Main Content */}
            <article>
              <header className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <Link
                    to={`/category/${article.category}`}
                    className="px-3 py-1 rounded-md text-xs font-semibold bg-primary-100 text-primary-700 hover:bg-primary-200 transition-colors whitespace-nowrap"
                  >
                    {article.categoryName}
                  </Link>
                  {article.isHot && (
                    <span className="px-2 py-1 rounded-md text-xs font-semibold bg-accent-100 text-accent-700">
                      热门
                    </span>
                  )}
                </div>
                <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-semibold text-primary-700">{article.author[0]}</span>
                    </div>
                    <span className="font-medium text-foreground-800">{article.author}</span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <span>{article.date}</span>
                  <span className="text-foreground-300">|</span>
                  <span>{article.views.toLocaleString()} 阅读</span>
                </div>
              </header>

              <div className="mb-8 rounded-xl overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-auto max-h-[500px] object-cover"
                />
              </div>

              <div
                className="prose prose-base md:prose-lg max-w-none text-foreground-800 leading-relaxed
                  [&>p]:mb-5 [&>p]:leading-8
                  [&>blockquote]:border-l-4 [&>blockquote]:border-primary-400 [&>blockquote]:bg-primary-50/50 [&>blockquote]:px-6 [&>blockquote]:py-4 [&>blockquote]:rounded-r-lg [&>blockquote]:my-6
                  [&>blockquote>p]:text-foreground-700 [&>blockquote>p]:italic [&>blockquote>p]:m-0
                  [&>h2]:font-heading [&>h2]:text-xl [&>h2]:font-bold [&>h2]:mt-8 [&>h2]:mb-4
                  [&>h3]:font-heading [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:mt-6 [&>h3]:mb-3"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-10 pt-8 border-t border-background-200/70">
                <h3 className="font-heading font-semibold text-sm text-foreground-700 mb-3">文章标签</h3>
                <div className="flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-100 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-700 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="space-y-8">
              {/* Author Card */}
              <div className="rounded-xl bg-background-100 p-5">
                <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-4">关于作者</h4>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="font-bold text-primary-700">{article.author[0]}</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm text-foreground-950">{article.author}</p>
                    <p className="text-xs text-foreground-500">资深编辑</p>
                  </div>
                </div>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  亚洲综合网资深编辑，长期关注亚洲{article.categoryName}领域动态，致力于为读者提供专业、深度的资讯报道。
                </p>
              </div>

              {/* Related Articles */}
              {relatedArticles.length > 0 && (
                <div className="rounded-xl bg-background-100 p-5">
                  <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-4">相关推荐</h4>
                  <div className="space-y-1">
                    {relatedArticles.map((ra) => (
                      <ArticleCard key={ra.id} article={ra} variant="compact" />
                    ))}
                  </div>
                </div>
              )}

              {/* Popular Tags */}
              <div className="rounded-xl bg-background-100 p-5">
                <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-4">热门标签</h4>
                <div className="flex flex-wrap gap-2">
                  {['亚洲','科技','财经','娱乐','生活','AI','数字货币','旅游','电影','电竞'].map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 rounded-full text-xs font-medium bg-background-50 text-foreground-600 hover:bg-secondary-100 hover:text-secondary-700 transition-colors cursor-pointer"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}